<template>
  <div class="gat">
    <div class="gat__aura" aria-hidden="true"></div>

    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" />
    </div>

    <div class="gat__body">
      <header class="cmd">
        <div class="cmd__id">
          <div class="cmd__glyph" aria-hidden="true">🛑</div>
          <div class="cmd__copy">
            <h1 class="cmd__title">Control de pluma</h1>
            <p class="cmd__sub">
              Apertura y cierre remoto
              <span class="cmd__sep" aria-hidden="true"></span>
              <span v-if="lastCommand" class="cmd__stamp">
                Último comando {{ formatClock(lastCommand.issued_at) }}
              </span>
              <span v-else class="cmd__stamp cmd__stamp--off">Sin comandos</span>
            </p>
          </div>
        </div>

        <div class="cmd__actions">
          <router-link class="act act--ghost" to="/logs/entradas-y-salidas">
            Entradas y salidas
          </router-link>
          <button class="act act--ghost" @click="goBack">Volver</button>
        </div>
      </header>

      <div v-if="busy || loading" class="scan" aria-hidden="true"></div>

      <transition name="drop">
        <p v-if="error" class="banner banner--error">{{ error }}</p>
      </transition>
      <transition name="drop">
        <p v-if="notice" class="banner banner--ok">{{ notice }}</p>
      </transition>

      <section class="console">
        <p class="console__hint">
          «Abrir» libera la pluma un momento y vuelve a bajar sola. «Cerrar» la regresa a su
          estado normal.
        </p>

        <div class="console__pads">
          <button
            class="pad pad--open"
            :disabled="busy"
            :aria-busy="pending === 'open'"
            @click="confirmCommand('open')"
          >
            <span class="pad__glyph" aria-hidden="true">
              <span v-if="pending === 'open'" class="pad__spin"></span>
              <span v-else>⬆</span>
            </span>
            <span class="pad__label">{{ pending === 'open' ? 'Abriendo…' : 'Abrir pluma' }}</span>
            <span class="pad__hint">Libera la pluma</span>
          </button>

          <button
            class="pad pad--close"
            :disabled="busy"
            :aria-busy="pending === 'close'"
            @click="confirmCommand('close')"
          >
            <span class="pad__glyph" aria-hidden="true">
              <span v-if="pending === 'close'" class="pad__spin"></span>
              <span v-else>⬇</span>
            </span>
            <span class="pad__label">{{ pending === 'close' ? 'Cerrando…' : 'Cerrar pluma' }}</span>
            <span class="pad__hint">Regresa a estado normal</span>
          </button>
        </div>

        <p class="console__note">Cada comando queda registrado con tu usuario.</p>
      </section>

      <section class="log">
        <div class="log__head">
          <h2 class="log__title">Historial de comandos</h2>
          <button class="act act--ghost act--sm" :disabled="loading" @click="fetchHistory">
            {{ loading ? 'Cargando…' : 'Actualizar' }}
          </button>
        </div>

        <div class="log__scroll">
          <table class="logtable">
            <thead>
              <tr>
                <th>Fecha y hora</th>
                <th>Comando</th>
                <th>Usuario</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in history" :key="row.id">
                <td class="mono">{{ formatDateTime(row.issued_at) }}</td>
                <td>
                  <span :class="['tag', row.command === 'open' ? 'tag--in' : 'tag--out']">
                    {{ row.command_label }}
                  </span>
                </td>
                <td>
                  <span v-if="row.issued_by" class="cell-name">{{ row.issued_by }}</span>
                  <span v-else class="cell-muted">—</span>
                </td>
                <td>
                  <span v-if="row.succeeded" class="tag tag--ok">Ejecutado</span>
                  <span v-else class="tag tag--deny" :title="row.error">Falló</span>
                </td>
              </tr>
              <tr v-if="!loading && !history.length" class="logtable__blank">
                <td colspan="4">
                  <p class="empty">Todavía no se ha enviado ningún comando.</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <transition name="drop">
      <div v-if="confirming" class="modal" @click.self="cancelCommand">
        <div class="modal__box" role="dialog" aria-modal="true">
          <h3 class="modal__title">
            {{ confirming === 'open' ? '¿Abrir la pluma?' : '¿Cerrar la pluma?' }}
          </h3>
          <p class="modal__body">
            Esto mueve la pluma físicamente en la entrada. Asegúrate de que no haya ninguna
            unidad debajo.
          </p>
          <div class="modal__actions">
            <button class="act act--ghost" @click="cancelCommand">Cancelar</button>
            <button
              :class="['act', confirming === 'open' ? 'act--go' : 'act--stop']"
              :disabled="busy"
              @click="sendCommand"
            >
              {{ confirming === 'open' ? 'Sí, abrir' : 'Sí, cerrar' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'GateControl',
  data() {
    return {
      history: [],
      loading: false,
      pending: '',
      confirming: '',
      error: '',
      notice: '',
      noticeTimer: null,
    }
  },
  created() {
    this.fetchHistory()
  },
  beforeUnmount() {
    if (this.noticeTimer) clearTimeout(this.noticeTimer)
  },
  computed: {
    busy() {
      return Boolean(this.pending)
    },
    lastCommand() {
      return this.history[0] || null
    },
  },
  methods: {
    apiBase() {
      return process.env.API_DIRECTORY || ''
    },
    authHeaders() {
      return { Authorization: `Token ${localStorage.getItem('authToken')}` }
    },
    goBack() {
      this.$router.push({ path: '/logs' })
    },
    handleError(err, fallback) {
      if (err?.response?.status === 403) {
        this.error = 'No tienes permisos para operar la pluma.'
        return
      }
      this.error = err?.response?.data?.detail || fallback
    },
    flashNotice(message) {
      this.notice = message
      if (this.noticeTimer) clearTimeout(this.noticeTimer)
      this.noticeTimer = setTimeout(() => {
        this.notice = ''
      }, 6000)
    },
    /**
     * The barrier is a physical object at the entrance, so a misclick can drop
     * it on a vehicle. Both commands go through a confirmation step.
     */
    confirmCommand(command) {
      this.error = ''
      this.confirming = command
    },
    cancelCommand() {
      this.confirming = ''
    },
    async sendCommand() {
      const command = this.confirming
      if (!command) return
      this.confirming = ''
      this.pending = command
      this.error = ''
      try {
        const { data } = await axios.post(
          `${this.apiBase()}/vehicle-access/gate/command`,
          { command },
          { headers: this.authHeaders() },
        )
        this.flashNotice(data.detail || 'Comando enviado.')
      } catch (err) {
        this.handleError(err, 'No se pudo enviar el comando a la pluma.')
      } finally {
        this.pending = ''
        // The attempt is recorded whether or not the controller accepted it, so
        // the history is refreshed on failure too.
        this.fetchHistory()
      }
    },
    async fetchHistory() {
      this.loading = true
      try {
        const { data } = await axios.get(`${this.apiBase()}/vehicle-access/gate/history`, {
          headers: this.authHeaders(),
          params: { limit: 20 },
        })
        this.history = data.results || []
      } catch (err) {
        this.handleError(err, 'No se pudo cargar el historial de comandos.')
      } finally {
        this.loading = false
      }
    },
    formatClock(value) {
      if (!value) return '—'
      return new Date(value).toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
  },
}
</script>

<!--
  Selectors are namespaced under `.gat` for the same reason as the vehicle
  access board: Bootstrap is loaded globally and owns `.btn`, `.table` and
  friends, so unprefixed names inherit its gutters and colors.
-->
<style scoped>
.gat {
  --gat-brand: #ffc434;
  --gat-brand-ink: #4d3600;
  --gat-strong: #0b1220;
  --gat-ink: #1e2a44;
  --gat-muted: #5b667f;
  --gat-faint: #8a93a8;
  --gat-line: rgba(15, 27, 51, 0.1);
  --gat-line-soft: rgba(15, 27, 51, 0.07);
  --gat-panel: rgba(255, 255, 255, 0.88);
  --gat-in: #12b76a;
  --gat-in-ink: #027a48;
  --gat-danger: #f04438;
  --gat-danger-ink: #b42318;
  --gat-warn-ink: #b54708;
  --gat-ring: rgba(37, 99, 235, 0.28);
  --gat-ease: cubic-bezier(0.2, 0.8, 0.2, 1);
  --gat-topbar: 62px;

  /* Anchored to the document rather than the flow: App.vue pushes #app down,
     but unscoped `#app { margin-top: 0 !important }` rules from other screens
     survive once visited, so the flow offset is not knowable. */
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  min-height: 100vh;
  background: #eef2fb;
  color: var(--gat-ink);
  color-scheme: light;
  /* App.vue sets text-align: center on #app for the whole app. */
  text-align: left;
  padding-bottom: 56px;
}

.gat::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(15, 27, 51, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15, 27, 51, 0.05) 1px, transparent 1px);
  background-size: 46px 46px;
  -webkit-mask-image: radial-gradient(125% 95% at 50% 0%, #000 22%, transparent 78%);
  mask-image: radial-gradient(125% 95% at 50% 0%, #000 22%, transparent 78%);
}
.gat__aura {
  position: fixed;
  inset: -18%;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(36% 30% at 16% 8%, rgba(255, 196, 52, 0.3), transparent 70%),
    radial-gradient(34% 28% at 86% 4%, rgba(37, 99, 235, 0.16), transparent 70%),
    radial-gradient(40% 34% at 62% 96%, rgba(240, 68, 56, 0.1), transparent 72%);
  animation: gat-drift 26s ease-in-out infinite alternate;
}
.gat__body {
  position: relative;
  z-index: 2;
  max-width: 1080px;
  margin: 0 auto;
  padding: calc(var(--gat-topbar) + 24px) 24px 0;
}

.nav-bar-operations {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--gat-topbar);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.05), 0 10px 26px rgba(72, 50, 8, 0.14);
}
/* Several components ship an unscoped rule that absolutely centers
   .logo-button on screen; it leaks in once any of them has been visited. */
.logo-button {
  position: static !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
  height: 60%;
  width: auto;
  object-fit: contain;
}

/* ---------- Command header ---------- */
.cmd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  padding: 16px 18px;
  border: 1px solid var(--gat-line);
  border-radius: 20px;
  background:
    linear-gradient(120deg, rgba(255, 196, 52, 0.16), transparent 44%),
    var(--gat-panel);
  backdrop-filter: blur(14px);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
  animation: gat-rise 0.5s var(--gat-ease) both;
}
.cmd__id {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.cmd__glyph {
  position: relative;
  width: 46px;
  height: 46px;
  flex: none;
  border-radius: 14px;
  display: grid;
  place-items: center;
  font-size: 1.25rem;
  background: linear-gradient(140deg, var(--gat-brand), #f0a500);
  box-shadow: 0 8px 20px rgba(240, 165, 0, 0.34);
}
.cmd__glyph::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 1px solid rgba(255, 255, 255, 0.75);
  animation: gat-halo 3.2s ease-out infinite;
}
.cmd__copy { min-width: 0; }
.cmd__title {
  margin: 0;
  font-size: 1.28rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--gat-strong);
}
.cmd__sub {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: var(--gat-muted);
}
.cmd__sep {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--gat-faint);
}
.cmd__stamp {
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid var(--gat-line);
  background: #fff;
  font-size: 0.76rem;
  color: var(--gat-ink);
  font-variant-numeric: tabular-nums;
}
.cmd__stamp--off { color: var(--gat-warn-ink); background: #fffaeb; border-color: #fec84b; }
.cmd__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* ---------- Buttons ---------- */
.gat .act {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid transparent;
  border-radius: 12px;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  transition: transform 0.2s var(--gat-ease), box-shadow 0.2s ease,
    background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}
.gat .act--sm { padding: 8px 13px; font-size: 0.82rem; }
.gat .act:hover:not(:disabled) { transform: translateY(-2px); }
.gat .act:active:not(:disabled) { transform: translateY(0) scale(0.985); }
.gat .act:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gat-ring);
}
.gat .act:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.gat .act--ghost {
  background: #fff;
  border-color: var(--gat-line);
  color: var(--gat-ink);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}
.gat .act--ghost:hover:not(:disabled) {
  background: #f7f9fc;
  border-color: rgba(15, 27, 51, 0.22);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
}
.gat .act--go {
  background: linear-gradient(180deg, #32d583, var(--gat-in));
  color: #023f28;
  box-shadow: 0 8px 20px rgba(18, 183, 106, 0.32);
}
.gat .act--stop {
  background: linear-gradient(180deg, #f97066, var(--gat-danger));
  color: #fff;
  box-shadow: 0 8px 20px rgba(240, 68, 56, 0.32);
}

.scan {
  position: relative;
  height: 2px;
  margin-top: 14px;
  border-radius: 999px;
  background: rgba(15, 27, 51, 0.08);
  overflow: hidden;
}
.scan::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 34%;
  background: linear-gradient(90deg, transparent, var(--gat-brand), transparent);
  animation: gat-scan 1.15s ease-in-out infinite;
}

/* ---------- Banners ---------- */
.banner {
  margin: 14px 0 0;
  padding: 11px 15px;
  border-radius: 14px;
  border: 1px solid;
  font-size: 0.87rem;
}
.banner--error { background: #fef3f2; border-color: #fda29b; color: var(--gat-danger-ink); }
.banner--ok { background: #ecfdf3; border-color: #6ce9a6; color: var(--gat-in-ink); }

/* ---------- Console ---------- */
.console {
  margin-top: 16px;
  padding: 20px;
  border: 1px solid var(--gat-line);
  border-radius: 20px;
  background: var(--gat-panel);
  backdrop-filter: blur(12px);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
  animation: gat-rise 0.55s var(--gat-ease) both;
}
.console__hint { margin: 0 0 18px; font-size: 0.88rem; line-height: 1.55; color: var(--gat-muted); }
.console__note { margin: 16px 0 0; font-size: 0.76rem; color: var(--gat-faint); }
/* Grid rather than a flex row: flex items floor at their min-content width and
   overflow a narrow viewport instead of wrapping. */
.console__pads {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
}

.pad {
  --accent: var(--gat-in);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 148px;
  padding: 22px 18px;
  border: 1px solid var(--gat-line);
  border-radius: 18px;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.07);
  transition: transform 0.24s var(--gat-ease), box-shadow 0.24s ease, border-color 0.24s ease;
}
.pad::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--accent), transparent 78%);
}
.pad:hover:not(:disabled) {
  transform: translateY(-4px);
  border-color: rgba(15, 27, 51, 0.18);
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.14);
}
.pad:active:not(:disabled) { transform: translateY(-1px) scale(0.99); }
.pad:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--gat-ring); }
.pad:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
.pad--open { --accent: var(--gat-in); }
.pad--close { --accent: var(--gat-danger); }
.pad__glyph {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  font-size: 1.3rem;
  color: #fff;
  background: var(--accent);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18);
}
.pad__label {
  font-size: 1.02rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--gat-strong);
}
.pad__hint { font-size: 0.76rem; color: var(--gat-faint); }
.pad__spin {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  animation: gat-spin 0.7s linear infinite;
}

/* ---------- History ---------- */
.log {
  margin-top: 18px;
  border: 1px solid var(--gat-line);
  border-radius: 20px;
  background: var(--gat-panel);
  backdrop-filter: blur(12px);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
  animation: gat-rise 0.6s var(--gat-ease) both;
}
.log__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border-bottom: 1px solid var(--gat-line);
}
.log__title { margin: 0; font-size: 0.98rem; font-weight: 800; color: var(--gat-strong); }
/* Bounded height so the sticky header has something to stick to, and
   horizontally scrollable so the four columns never widen the page. */
.log__scroll {
  max-height: min(60vh, 620px);
  overflow: auto;
  border-radius: 0 0 20px 20px;
}
.gat .logtable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.85rem;
  color: var(--gat-ink);
}
.gat .logtable th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 13px 14px;
  text-align: left;
  font-size: 0.71rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gat-muted);
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid var(--gat-line);
  backdrop-filter: blur(10px);
}
.gat .logtable td {
  padding: 11px 14px;
  border-bottom: 1px solid var(--gat-line-soft);
  vertical-align: middle;
}
.gat .logtable tbody tr {
  transition: background-color 0.2s ease;
  animation: gat-row 0.42s var(--gat-ease) both;
}
.gat .logtable tbody tr:nth-child(2) { animation-delay: 0.02s; }
.gat .logtable tbody tr:nth-child(3) { animation-delay: 0.04s; }
.gat .logtable tbody tr:nth-child(4) { animation-delay: 0.06s; }
.gat .logtable tbody tr:nth-child(5) { animation-delay: 0.08s; }
.gat .logtable tbody tr:nth-child(n + 6) { animation-delay: 0.1s; }
.gat .logtable tbody tr:hover td { background: #f4f7fc; }
.logtable__blank td { border-bottom: none; }
.logtable__blank .empty { margin: 12px; }
.empty { color: var(--gat-muted); font-size: 0.87rem; }
.mono { font-variant-numeric: tabular-nums; white-space: nowrap; }
.cell-name { font-weight: 700; color: var(--gat-strong); }
.cell-muted { color: var(--gat-faint); }

.tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid transparent;
}
.tag--in { background: #ecfdf3; color: var(--gat-in-ink); border-color: #a6f4c5; }
.tag--out { background: #fef3f2; color: var(--gat-danger-ink); border-color: #fecdca; }
.tag--ok { background: #f2f4f7; color: #475467; border-color: #e4e7ec; }
.tag--deny { background: #fef3f2; color: var(--gat-danger-ink); border-color: #fda29b; cursor: help; }

/* ---------- Confirmation ---------- */
.modal {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(11, 18, 32, 0.55);
  backdrop-filter: blur(3px);
}
.modal__box {
  width: 100%;
  max-width: 430px;
  padding: 22px;
  border: 1px solid var(--gat-line);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 26px 60px rgba(15, 23, 42, 0.28);
}
.modal__title { margin: 0 0 10px; font-size: 1.1rem; font-weight: 800; color: var(--gat-strong); }
.modal__body { margin: 0 0 20px; font-size: 0.88rem; line-height: 1.55; color: var(--gat-muted); }
.modal__actions { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }

/* ---------- Transitions ---------- */
.drop-enter-active,
.drop-leave-active { transition: opacity 0.28s ease, transform 0.28s var(--gat-ease); }
.drop-enter-from,
.drop-leave-to { opacity: 0; transform: translateY(-8px); }

/* ---------- Keyframes ---------- */
@keyframes gat-rise {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: none; }
}
@keyframes gat-row {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
}
@keyframes gat-drift {
  from { transform: translate3d(-1.5%, -1%, 0) scale(1); }
  to { transform: translate3d(2%, 1.5%, 0) scale(1.07); }
}
@keyframes gat-spin {
  to { transform: rotate(360deg); }
}
@keyframes gat-scan {
  0% { transform: translateX(-110%); }
  100% { transform: translateX(400%); }
}
@keyframes gat-halo {
  0% { opacity: 0.7; transform: scale(1); }
  70% { opacity: 0; transform: scale(1.28); }
  100% { opacity: 0; transform: scale(1.28); }
}

@media (max-width: 720px) {
  .gat__body { padding: calc(var(--gat-topbar) + 18px) 14px 0; }
  .cmd { padding: 14px; }
  .cmd__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }
  .gat .cmd__actions .act { width: 100%; }
  .console { padding: 16px; }
  /* One pad per row: these move a physical barrier, so they should not sit
     shoulder to shoulder under a thumb. */
  .console__pads { grid-template-columns: 1fr; }
  .pad { min-height: 128px; }
  .modal__actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gat .modal__actions .act { width: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .gat *,
  .gat *::before,
  .gat *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }
}
</style>
